import React, { useState, useEffect } from 'react';
import Ability from "./Ability";
import DetailsContent from './DetailsContent';
import "./CardDetails.css";
import { BrowserView, MobileView } from "react-device-detect";

const CardDetails = ({ cardDetails, onClose, abilities, disableTopOffset = false }) => {
    const escapeImagePath = (path) => path ? String(path).replace(/'/g, "\\'") : path;

    // Helper: ensure the provided image URL uses the HD subfolder after EN/
    const insertHdSegment = (url) => {
        if (!url) return url;
        try {
            const s = String(url);
            // If already contains /EN/hd/ keep as-is
            if (s.includes('/EN/hd/')) return s;
            // If it contains /EN/ insert hd/ after it
            if (s.includes('/EN/')) return s.replace('/EN/', '/EN/hd/');
            // If the url doesn't have EN, return as-is
            return s;
        } catch (e) {
            return url;
        }
    };

    // Helper: remove hd segment if present (useful for background images)
    const removeHdSegment = (url) => {
        if (!url) return url;
        try {
            const s = String(url);
            return s.replace('/EN/hd/', '/EN/');
        } catch (e) {
            return url;
        }
    };

    // Build the detail (large) image path. Prefer server-provided imageUrl when available
    let cardImagePath;
    if (cardDetails.imageUrl) {
        cardImagePath = insertHdSegment(cardDetails.imageUrl);
    } else {
        const editionVal = Array.isArray(cardDetails.edition) ? cardDetails.edition[0] : cardDetails.edition;
        const safeName = String(cardDetails.name || '').toLowerCase().replace(/ /g, '_');
        cardImagePath = `/assets/cards/${editionVal}/EN/hd/${cardDetails.cardNumber}_${safeName}_en.png`;
    }

    // Build the background image path (non-hd) — prefer imageUrl without hd if available
    let backgroundCardImagePath;
    if (cardDetails.imageUrl) {
        backgroundCardImagePath = removeHdSegment(cardDetails.imageUrl);
    } else {
        const editionVal = Array.isArray(cardDetails.edition) ? cardDetails.edition[0] : cardDetails.edition;
        const safeName = String(cardDetails.name || '').toLowerCase().replace(/ /g, '_');
        backgroundCardImagePath = `/assets/cards/${editionVal}/EN/${cardDetails.cardNumber}_${safeName}_en.png`;
    }
    const cardBackgroundPath = escapeImagePath(backgroundCardImagePath);

    const [showAbilityModal, setShowAbilityModal] = useState(false);
    const [navbarHeight, setNavbarHeight] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight); // Hauteur du viewport
    const [abilitiesHeight, setAbilitiesHeight] = useState(0); // Hauteur des abilities
    const [selectedAbility, setSelectedAbility] = useState(null); // Ajout de l'état selectedAbility
    const [isZoomed, setIsZoomed] = useState(false); // état du zoom

    useEffect(() => {
        const navbar = document.querySelector('nav.navbar');
        if (navbar) setNavbarHeight(navbar.offsetHeight);

        const handleResize = () => {
            setViewportHeight(window.innerHeight); // Réactualise la hauteur du viewport à chaque redimensionnement
            if (navbar) setNavbarHeight(navbar.offsetHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calcule la hauteur totale de la carte, en soustrayant la navbar et 10% de la hauteur, et si des Abilities existent, leur hauteur
    const cardMaxHeight = (abilities) => {
        const abilitiesHeight = abilities.length > 0 ? 55 : 0;  // Hauteur d'une ligne d'abilities (55px si présentes)
        return `${viewportHeight - navbarHeight - 0.1 * viewportHeight - abilitiesHeight}px`;
    };

    const closeAbilityModal = () => {
        setShowAbilityModal(false);
        setSelectedAbility(null); // Réinitialiser l'ability sélectionnée lors de la fermeture de la modale
    };

    // Fonction pour afficher la modale d'Ability
    const handleAbilityClick = (ability) => {
        setSelectedAbility(ability); // Mettre à jour l'ability sélectionnée
        setShowAbilityModal(true); // Afficher la modale
    };

    const handleCardClick = () => {
    setIsZoomed(true); // ouvre la modale de zoom
  };

  const handleZoomClose = () => {
    setIsZoomed(false); // ferme la modale de zoom
  };

    // Always use top:0 for mobile. If disableTopOffset is true, force top:0 on desktop too.
    let modalTopStyle = { top: '0' };
    try {
        if (typeof window !== 'undefined' && !disableTopOffset) {
            const isLargeScreen = window.matchMedia && window.matchMedia('(min-width: 992px)').matches;
            if (isLargeScreen) {
                const topBar = document.querySelector('.top-search-bar');
                if (topBar) {
                    const cs = window.getComputedStyle(topBar);
                    const visible = cs && cs.display !== 'none' && topBar.offsetHeight > 0;
                    if (visible) {
                        modalTopStyle = { top: `calc(var(--topbar-height, 0px) + env(safe-area-inset-top, 0px))` };
                    }
                }
            }
        }
    } catch (e) {
        // ignore and fallback to top: 0
    }

    return (
        <>
        <div className="card-details-component">
            {/* Background flouté */}
            <div className="modal-fullscreen modal-detail" style={{ '--card-image-path': `url(${backgroundCardImagePath})`, ...modalTopStyle }}>
                <div className="card-content-container d-flex flex-column flex-lg-row">
                    {/* Carte à gauche */}
                    <div className="card-left text-center">
                        <MobileView>
                            <div className="modal-body-card d-flex justify-content-center align-items-center"
                            onClick={handleCardClick} style={{ cursor: "zoom-in" }}>
                                <img className="card-image img-fluid" src={cardImagePath} alt={cardDetails.name} />
                            </div>
                            {/* Si des abilities existent, les afficher sous la carte */}
                            {cardDetails.abilities && (
                                <div className="abilities-row mt-2">
                                    {cardDetails.abilities.map((ability, idx) => {
                                        const match = Array.isArray(abilities) ? abilities.find(a => a.name === ability) : null;
                                        const imgSrc = match && match.image ? match.image : `/assets/icons/abilities/${ability}.png`;
                                        return (
                                            <img
                                                key={idx}
                                                className="ability-icon"
                                                src={imgSrc}
                                                alt={ability}
                                                onClick={() => handleAbilityClick(ability)}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </MobileView>
                        <BrowserView>
                            <div className="modal-body-card d-flex justify-content-center align-items-center"
                            onClick={handleCardClick} style={{ cursor: "zoom-in" }}>
                                <img className="card-image img-fluid" src={cardImagePath} alt={cardDetails.name} />
                            </div>
                        </BrowserView>
                    </div>

                    {/* Séparateur */}
                    <div className="p-1"></div>

                    {/* Infos carte à droite */}
                    <MobileView>
                        <div className="card-right flex-grow-1 p-6"
                            style={{
                                minHeight: '40vh',
                                maxHeight: 'calc(100vh - 56px)', // 56px = typical mobile navbar/footer height
                                overflowY: 'auto',
                                paddingBottom: '80px', // ensure content is not hidden under nav/footer
                            }}
                        >
                            <h1>{cardDetails.name}</h1>
                            {cardDetails.types && <p>Type: {cardDetails.types.join(' - ')}</p>}
                            Subtype: {cardDetails.subtypes && cardDetails.subtypes.length > 0 
                                ? cardDetails.subtypes.join(' - ') 
                                : "-"}
                            <DetailsContent
                                cardDetails={cardDetails}
                                abilities={abilities}
                                onClose={onClose}
                            />
                        </div>
                    </MobileView>
                        <BrowserView>
                        <div className="card-right flex-grow-1 p-6"
                            style={{
                                maxHeight: cardMaxHeight(cardDetails.abilities), // Applique la hauteur calculée
                                overflowY: 'auto',  // Permet de scroller si le contenu dépasse
                                
                            }}
                        >
                            <h1>{cardDetails.name}</h1>
                        {cardDetails.types && <p>Type: {cardDetails.types.join(' - ')}</p>}
                        Subtype: {cardDetails.subtypes && cardDetails.subtypes.length > 0 
                        ? cardDetails.subtypes.join(' - ') 
                        : "-"}

                        <DetailsContent
                            cardDetails={cardDetails}
                            abilities={abilities}
                            onClose={onClose}
                        />
                    </div>
                    {cardDetails.abilities && (
                        <div className="abilities-row mt-3">
                            {cardDetails.abilities.map((ability, idx) => {
                                const match = Array.isArray(abilities) ? abilities.find(a => a.name === ability) : null;
                                const imgSrc = match && match.image ? match.image : `/assets/icons/abilities/${ability}.png`;
                                return (
                                    <img
                                        key={idx}
                                        className="ability-icon"
                                        src={imgSrc}
                                        alt={ability}
                                        onClick={() => handleAbilityClick(ability)}
                                    />
                                );
                            })}
                        </div>
                        )}
                        </BrowserView>
                </div>

                {/* Bouton de fermeture grand écran */}
                <button className="close-modal-btn" onClick={onClose}>&times;</button>

                {/* Modal d’Ability si sélectionnée */}
                {showAbilityModal && selectedAbility && (
                    <Ability ability={selectedAbility} onClose={closeAbilityModal} abilities={abilities} />
                )}
            </div>
        </div>

      {/* Deuxième modale (zoom plein écran) */}
      {isZoomed && (
        <div
          className="zoom-modal"
          onClick={handleZoomClose} // clic = fermer
        >
          <img
            className="zoomed-card"
            src={cardImagePath}
            alt={cardDetails.name}
          />
        </div>
      )}
      </>
    );
};

export default CardDetails;
